import { useState, useEffect, useMemo, useRef, type ReactNode } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCredits } from "@/contexts/CreditContext";
import { useWhiteLabel } from "@/contexts/WhiteLabelContext";
import { useGetPath } from "@/hooks/useGetPath";
import { useLiveDemo } from "@/hooks/useLiveDemo";
import { hasVpcApiKey } from "@/utils/vpcMode";
import {
  FileText,
  History,
  Mail,
  PanelLeftClose,
  ClipboardCheck,
  LayoutList,
  Activity,
  Gauge,
  Rocket,
  LayoutGrid,
  Sparkles,
  Microscope,
  Database,
  X,
} from "lucide-react";
import { p3OrgConfig } from "@/config";
import { canAccessEval, isNomadicOrgUser } from "@/pages/use-cases/evaluation/shared";

// ============================================================================
// Types
// ============================================================================

interface NavItem {
  id: string;
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  isExternal?: boolean;
  /** Optional sub-navigation shown indented when this item is active. */
  subItems?: { id: string; name: string; href: string }[];
}

interface NavSection {
  label?: string;
  items: NavItem[];
}

interface NewSideBarProps {
  onCollapse?: () => void;
  isCollapsed?: boolean;
  onNavigate?: () => void;
  /**
   * Mobile drawer mode: the nav fills the panel it's dropped into and swaps the
   * collapse control for a close (X) that sits exactly where the top bar's
   * burger was, so the burger reads as turning into a cross.
   */
  onClose?: () => void;
}

// ============================================================================
// Navigation Configuration
// ============================================================================

// Dataset Marketplace is public: admins get the full curation surface,
// non-admins get a read-only teaser.
const DATASET_MARKETPLACE_ITEM: NavItem = {
  id: "dataset-marketplace",
  name: "Dataset Studio",
  href: "/dataset-studio",
  icon: Database,
};

const HISTORY_ITEM: NavItem = { id: "history", name: "History", href: "/history", icon: History };

// Shown to Nomadic admins and orgs enrolled in the eval beta (see
// canAccessEval in pages/use-cases/evaluation/shared.tsx) — not gated behind
// admin_mode alone, which is not a reliable proxy for "internal".
const EVALUATE_ITEM: NavItem = {
  id: "evaluation",
  name: "Evaluate",
  href: "/use-cases/evaluation",
  icon: ClipboardCheck,
  subItems: [
    { id: "eval-history", name: "Recent Evaluations", href: "/use-cases/evaluation?view=history" },
    { id: "eval-search", name: "Search Evaluation", href: "/use-cases/evaluation?view=search" },
    { id: "eval-jobs", name: "Evaluation Jobs", href: "/use-cases/evaluation?view=jobs" },
  ],
};

// Benchmarks sub-item is Nomadic-admin-only (isNomadicOrgUser) — hidden from
// beta customer orgs that can otherwise see the Evaluate section.
const EVAL_BENCHMARKS_SUBITEM = {
  id: "eval-benchmarks",
  name: "Benchmarks",
  href: "/use-cases/evaluation?view=benchmarks",
};

/** Start opens the dataset homepage. */
function usePlaygroundSection(isLiveDemo: boolean, canEval: boolean, showBenchmarks: boolean): NavSection {
  return useMemo(
    () => {
      const evaluateItem: NavItem = showBenchmarks
        ? { ...EVALUATE_ITEM, subItems: [...(EVALUATE_ITEM.subItems ?? []), EVAL_BENCHMARKS_SUBITEM] }
        : EVALUATE_ITEM;
      return {
        label: "Playground",
        items: [
          { id: "uploads", name: "Start", href: "/", icon: Rocket },
          { id: "examples", name: "Examples", href: "/examples", icon: LayoutGrid },
          { id: "events", name: "Analyze", href: "/events", icon: Microscope },
          DATASET_MARKETPLACE_ITEM,
          // History needs a real backing account, so it's hidden in the
          // anonymous, unauthenticated live demo. Evaluate is further
          // restricted to admins/beta orgs via canEval; Benchmarks within it
          // is Nomadic-admin-only via showBenchmarks.
          ...(!isLiveDemo ? [HISTORY_ITEM] : []),
          ...(!isLiveDemo && canEval ? [evaluateItem] : []),
        ],
      };
    },
    [isLiveDemo, canEval, showBenchmarks]
  );
}

/** In-app release notes. Signed-in only, so it is hidden in the live demo. */
const WHATS_NEW_ITEM: NavItem = {
  id: "whats-new",
  name: "What's New",
  href: "/whats-new",
  icon: Sparkles,
};

const HELP_SECTION: NavSection = {
  label: "Help",
  items: [
    {
      id: "documentation",
      name: "Documentation",
      href: "https://docs.nomadicml.com",
      icon: FileText,
      isExternal: true,
    },
    {
      id: "contact_us",
      name: "Contact Us",
      href: "https://www.nomadicai.com/talk-to-sales",
      icon: Mail,
      isExternal: true,
    },
  ],
};

function useHelpSection(isLiveDemo: boolean): NavSection {
  return useMemo(
    () => ({
      ...HELP_SECTION,
      items: isLiveDemo ? HELP_SECTION.items : [WHATS_NEW_ITEM, ...HELP_SECTION.items],
    }),
    [isLiveDemo],
  );
}

// Admin-only section.
const ADMIN_SECTION: NavSection = {
  label: "Admin",
  items: [
    { id: "data-curation", name: "Data Curation", href: "/use-cases/data-curation", icon: LayoutList },
    { id: "admin-dashboard", name: "Dashboard", href: "/admin/dashboard", icon: Gauge },
    { id: "motion-coverage", name: "Motion Coverage", href: "/motion-coverage", icon: Activity },
  ],
};

// ============================================================================
// Utility Functions
// ============================================================================

/** Normalize path so /live-demo/events and /events both match the same tab */
const normalizePathForTab = (pathname: string): string =>
  pathname.startsWith("/live-demo")
    ? pathname.slice("/live-demo".length) || "/"
    : pathname;

const isEvalRunResultRoute = (pathname: string): boolean =>
  /^\/use-cases\/evaluation\/[^/]+\/runs\/[^/]+\/?$/.test(normalizePathForTab(pathname));

const getActiveTabFromPath = (pathname: string): string => {
  const path = normalizePathForTab(pathname);
  if (path === "/") return "uploads";
  if (path === "/start-page-2") return "start-page-2";
  if (path === "/start-page") return "uploads";
  if (path === "/examples") return "examples";
  if (path === "/dataset-studio" || path.startsWith("/dataset-studio/")) return "dataset-marketplace";
  if (path === "/events" || path.startsWith("/events/")) return "events";
  // The "New Analysis" launch page is the Analyze surface with a preselected
  // video set, not the Start page.
  if (/^\/use-cases\/rapid-review\/batch\/?$/.test(path)) return "events";
  if (path.startsWith("/live-streams")) return "live-streams";
  if (path === "/search" || path.startsWith("/search/")) return "search";
  if (path.startsWith("/use-cases/evaluation")) return "evaluation";
  if (path.startsWith("/use-cases/data-curation")) return "data-curation";
  if (path.startsWith("/admin/dashboard")) return "admin-dashboard";
  if (path.startsWith("/motion-coverage")) return "motion-coverage";
  if (path === "/history") return "history";
  if (path === "/whats-new") return "whats-new";
  if (path === "/profile" || path.startsWith("/profile")) return "profile";
  return "uploads";
};

const formatTrialCountdown = (isoDate?: string): string | null => {
  if (!isoDate) return null;
  const targetMs = Date.parse(isoDate);
  if (Number.isNaN(targetMs)) return null;

  const diffMs = targetMs - Date.now();
  if (diffMs <= 0) return "Expired";

  const totalMinutes = Math.floor(diffMs / (1000 * 60));
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

// ============================================================================
// Main Component
// ============================================================================

export default function NewSideBar({
  onCollapse,
  isCollapsed = false,
  onNavigate,
  onClose,
}: NewSideBarProps) {
  // Hooks
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const getPath = useGetPath();
  const isLiveDemo = useLiveDemo();
  // Mobile drawer: bigger type, bigger icons, 48px touch targets.
  const isDrawer = Boolean(onClose);
  const { profile } = useCredits();
  const { config } = useWhiteLabel();
  const playgroundSection = usePlaygroundSection(isLiveDemo, canAccessEval(profile), isNomadicOrgUser(profile));
  const helpSection = useHelpSection(isLiveDemo);

  // State
  const [activeTab, setActiveTab] = useState("uploads");
  // Custom tooltip for collapsed nav items (rendered fixed so it escapes the
  // sidebar's scroll overflow).
  const [navTip, setNavTip] = useState<{ name: string; y: number } | null>(null);
  // Toggling isCollapsed unmounts the hovered element without firing onMouseLeave,
  // so clear any stuck tooltip whenever the collapse state changes.
  useEffect(() => {
    setNavTip(null);
  }, [isCollapsed]);
  // Nav scrollbar is hidden until the user actually scrolls, then fades back out.
  const [isNavScrolling, setIsNavScrolling] = useState(false);
  const navScrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleNavScroll = () => {
    setIsNavScrolling(true);
    if (navScrollTimeoutRef.current) clearTimeout(navScrollTimeoutRef.current);
    navScrollTimeoutRef.current = setTimeout(() => setIsNavScrolling(false), 800);
  };
  useEffect(() => {
    return () => {
      if (navScrollTimeoutRef.current) clearTimeout(navScrollTimeoutRef.current);
    };
  }, []);
  // Computed values
  const isWhiteLabel = Boolean(
    config?.isEnabled ||
    // profile?.org_id === "org_86fc9717efd9" ||
    profile?.org_id === "org_atlas_pro" ||
    profile?.org_id === p3OrgConfig.orgId,
  );
  const showAdminSection = Boolean(profile?.admin_mode);

  // Update active tab based on route
  useEffect(() => {
    setActiveTab(getActiveTabFromPath(location.pathname));
  }, [location.pathname]);

  useEffect(() => {
    console.log("[NAV] Location changed to:", location.pathname, {
      timestamp: Date.now(),
    });

    const params = new URLSearchParams(location.search);
    const isWorkbenchShareLink =
      /^\/workbench\/(?!videos(?:\/|$))[^/]+(?:\/taxonomy)?\/?$/.test(location.pathname) &&
      Boolean(params.get("share_token") || params.get("token"));

    if (
      !user &&
      !isEvalRunResultRoute(location.pathname) &&
      !location.pathname.includes("live-demo") &&
      // Batch share links reach Workbench's API authorization without signing in.
      !isWorkbenchShareLink &&
      !hasVpcApiKey()
    ) {
      const returnTo = `${location.pathname}${location.search}${location.hash}`;
      const query = new URLSearchParams({ returnTo }).toString();
      navigate(`/login?${query}`);
    }
    // if live-demo, do not redirect to login
    if (user && location.pathname === "/login") {
      navigate("/");
    }
  }, [user, location.pathname, location.search, location.hash, navigate]);

  // Prefetch route chunks on hover so navigation feels instant
  const prefetchedRef = useRef<Set<string>>(new Set());
  const prefetchRoute = (itemId: string) => {
    if (prefetchedRef.current.has(itemId)) return;
    prefetchedRef.current.add(itemId);
    const prefetchMap: Record<string, () => Promise<unknown>> = {
      events: () => import("./VideosV2"),
      uploads: () => import("./StartPage3"),
      "start-page-2": () => import("./StartPage2"),
      search: () => import("./Search"),
      "dataset-marketplace": () => import("./DatasetMarketplace"),
      "live-streams": () => import("./MultiStreamViewer"),
      history: () => import("../pages/History"),
      "whats-new": () => import("../pages/WhatsNew"),
      "admin-dashboard": () => import("../pages/AdminDashboard"),
    };
    prefetchMap[itemId]?.();
  };

  // Handlers
  const handleNavigation = (item: NavItem) => {
    if (item.isExternal) {
      window.open(item.href, "_blank");
    } else {
      setActiveTab(item.id);
      navigate(getPath(item.href));
      onNavigate?.();
    }
    // Selecting a destination collapses the hover-expanded rail back to icons so
    // it doesn't stay open over the page the user just navigated to.
    if (!isCollapsed) onCollapse?.();
  };

  // Renderers
  const renderNavItem = (item: NavItem) => {
    const isActive = activeTab === item.id;
    const Icon = item.icon;
    const sidebarParams = new URLSearchParams(location.search);
    // Detail pages (runs/compare) carry the origin as ?fromView so the right
    // sub-item stays highlighted there too. Default to "history" — the
    // Evaluate page itself opens on Recent Evaluations when no ?view is set,
    // so the highlighted sub-item must match that landing tab.
    const currentView = sidebarParams.get("view") || sidebarParams.get("fromView") || "history";

    return (
      <div key={item.id} className="group relative">
        <button
          // Stop propagation so this click navigates rather than bubbling up to
          // the collapsed rail's expand handler (see the root container).
          onClick={(e) => {
            e.stopPropagation();
            handleNavigation(item);
          }}
          onMouseEnter={(e) => {
            prefetchRoute(item.id);
            if (isCollapsed) {
              const r = e.currentTarget.getBoundingClientRect();
              setNavTip({ name: item.name, y: r.top + r.height / 2 });
            }
          }}
          onMouseLeave={() => isCollapsed && setNavTip(null)}
          type="button"
          aria-label={item.name}
          aria-current={isActive ? "page" : undefined}
          data-testid={`sidebar-nav-item-${item.id}`}
          className={`w-full flex items-center rounded-[8px] transition-colors cursor-pointer ${
            isDrawer ? "min-h-[48px] font-medium" : ""
          } ${
            !isCollapsed && isActive
              ? "bg-[#f4f0e6] text-[#1F2753] font-medium"
              : !isCollapsed
                ? "text-[#4a4d5f] hover:bg-[#f1ebdf]"
                : "text-[#4a4d5f]"
          }`}
        >
          {/* Fixed-width leading icon slot: keeps every icon at the exact same
              x-position (aligned with the logo) whether collapsed or expanded. */}
          <span
            className={`${isDrawer ? "w-12" : "w-10"} flex-shrink-0 flex items-center justify-center py-0.5`}
          >
            <span
              className={`flex items-center justify-center ${isDrawer ? "w-10 h-10" : "w-9 h-9"} rounded-[10px] transition-colors ${
                isCollapsed && isActive
                  ? "bg-[#f4f0e6] text-[#1F2753]"
                  : isCollapsed
                    ? "group-hover:bg-[#f1ebdf]"
                    : ""
              }`}
            >
              <Icon className={`${isDrawer ? "w-[22px] h-[22px]" : "w-4 h-4"} flex-shrink-0`} />
            </span>
          </span>
          {!isCollapsed && (
            <span className={`flex-1 text-left truncate pr-3 ${isDrawer ? "text-[15px]" : "text-xs"}`}>
              {item.name}
            </span>
          )}
        </button>
        {item.subItems && isActive && !isCollapsed && (
          <div className="mt-1 ml-3 flex flex-col gap-0.5 border-l border-[#e0e0e0] pl-2">
            {item.subItems.map((sub) => {
              const subView = new URLSearchParams(sub.href.split("?")[1] || "").get("view");
              const subActive = subView === currentView;
              return (
                <button
                  key={sub.id}
                  onClick={() => {
                    navigate(getPath(sub.href));
                    onNavigate?.();
                    if (!isCollapsed) onCollapse?.();
                  }}
                  className={`w-full text-left rounded-[6px] px-3 transition-colors ${
                    isDrawer ? "py-2.5 text-sm" : "py-1.5 text-xs"
                  } ${
                    subActive
                      ? "bg-[#f4f0e6] text-[#1F2753] font-medium"
                      : "text-[#6b6e7d] hover:bg-[#f1ebdf]"
                  }`}
                >
                  {sub.name}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderSection = (section: NavSection, opts?: { first?: boolean }) => (
    <div className="mb-3">
      {section.label && (
        // Fixed-height label slot so nav items keep the same vertical position
        // whether the label shows as a divider (collapsed) or text (expanded).
        <div className={`${isDrawer ? "h-8" : "h-6"} flex items-center mb-1`}>
          {isCollapsed ? (
            // The divider is a separator between collapsed sections; the top
            // section needs none above it, so keep the (empty) slot for vertical
            // alignment but omit the line.
            opts?.first ? null : <div className="mx-3 flex-1 border-t border-[#e0e0e0]" />
          ) : (
            <h3
              className={`px-3 font-medium text-gray-400 uppercase tracking-wider ${
                isDrawer ? "text-[11px]" : "text-[10px]"
              }`}
            >
              {section.label}
            </h3>
          )}
        </div>
      )}
      <nav className={isDrawer ? "space-y-1" : "space-y-0.5"}>
        {section.items.map((item) => renderNavItem(item))}
      </nav>
    </div>
  );

  const renderHeader = () => {
    const displayName = isWhiteLabel ? config.companyName : "Nomadic AI";
    const logoSrc = isWhiteLabel ? config.logoUrl : "/Nomadic_Logo_Only_Latest.svg";

    // The mark navigates to the app's home page ("/", the Start page), with
    // the same side effects as picking Start from the nav below.
    const renderBrand = (className: string, children: ReactNode) => (
      <Link
        to={getPath("/")}
        aria-label="Go to home"
        className={className}
        onClick={() => {
          setActiveTab("uploads");
          onNavigate?.();
          if (!isCollapsed) onCollapse?.();
        }}
      >
        {children}
      </Link>
    );

    // Mobile drawer: close control first, then the mark — same order and
    // position as the top bar's burger + logo, so one turns into the other.
    if (onClose) {
      return (
        <div className="flex items-center h-12 flex-shrink-0 gap-2 px-2">
          <button
            onClick={onClose}
            aria-label="Close navigation"
            className="flex items-center justify-center h-9 w-9 rounded-lg text-[#1F2753] hover:bg-[#f1ebdf] transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          {renderBrand(
            "flex items-center gap-2 min-w-0",
            <>
              <img src={logoSrc} alt={displayName} className="h-5 w-5 object-contain flex-shrink-0" />
              <span className="text-sm font-semibold text-[#1F2753] truncate">{displayName}</span>
            </>,
          )}
        </div>
      );
    }

    // Fixed-height header so the first nav item sits at the same y whether the
    // sidebar is collapsed or expanded. The leading w-16 slot lines its content
    // up on the exact x-position of the nav icons below it.
    return (
      <>
        <div className="flex items-center h-12 flex-shrink-0">
          {isCollapsed ? (
            // Collapsed: just the logo. Hovering the rail expands it (handled by
            // the root container), so this slot needs no hover swap of its own.
            <div className="w-16 flex items-center justify-center flex-shrink-0">
              {renderBrand(
                "flex items-center",
                <img src={logoSrc} alt={displayName} className="h-6 w-6 object-contain" />,
              )}
            </div>
          ) : (
            // Expanded: logo (aligned with the nav icons below via pl-5) with the
            // wordmark right beside it, then the collapse control on the right.
            <>
              <div className="flex items-center pl-5 flex-1 min-w-0">
                {renderBrand(
                  "flex items-center gap-2 min-w-0",
                  <>
                    <img src={logoSrc} alt={displayName} className="h-6 w-6 object-contain flex-shrink-0" />
                    <span className="text-sm font-semibold text-[#1F2753] truncate">{displayName}</span>
                  </>,
                )}
              </div>
              {config.motto && config.mottoPosition === 'inline' && (
                <span className="text-xs text-gray-500 italic whitespace-nowrap">{config.motto}</span>
              )}
              {onCollapse && (
                <button
                  onClick={onCollapse}
                  className="flex items-center justify-center w-9 h-9 mr-3 rounded-[10px] hover:bg-[#f1ebdf] transition-colors flex-shrink-0 cursor-pointer"
                  aria-label="Collapse sidebar"
                >
                  <PanelLeftClose className="w-4 h-4 text-[#1F2753]" />
                </button>
              )}
            </>
          )}
        </div>

        {!isCollapsed && config.motto && config.mottoPosition !== 'inline' && (
          <p className="px-4 pb-2 text-xs text-gray-500 italic text-center">{config.motto}</p>
        )}
      </>
    );
  };

  const renderUserFooter = () => {
    if (!user || isLiveDemo) return null;

    const orgTrialCountdown = profile?.is_org_member
      ? formatTrialCountdown(profile?.org_trial_end_date)
      : null;
    const personalTrialCountdown = !profile?.is_org_member && profile?.plan === "trial"
      ? formatTrialCountdown(profile?.trial_end_date)
      : null;
    const trialCountdown = profile?.is_org_member ? orgTrialCountdown : personalTrialCountdown;

    let planLabel: string | null = null;
    if (profile?.is_org_member) {
      planLabel = orgTrialCountdown === "Expired"
        ? "Trial Expired"
        : orgTrialCountdown
        ? "Enterprise Trial"
        : "Enterprise";
    } else if (profile?.plan === "trial") {
      planLabel = personalTrialCountdown === "Expired" ? "Trial Expired" : "Free Trial";
    } else if (profile?.plan === "paid") {
      planLabel = "Paid";
    } else if (profile?.plan === "sponsored") {
      planLabel = "Sponsored";
    }

    // Trial-ish plans (active, enterprise, or expired) read as urgent; everything else is the default navy.
    const isTrialPlan = planLabel?.includes("Trial") ?? false;
    const planColorClass = isTrialPlan ? "text-red-400 bg-red-50" : "text-[#1F2753] bg-[#e9e2d4]";
    const secondaryLine = profile?.org_name
      || (trialCountdown && trialCountdown !== "Expired" ? `Remaining: ${trialCountdown}` : null);
    const initial = (user.email?.[0] ?? "?").toUpperCase();
    const goToProfile = () => navigate(getPath("/profile?tab=profile"));

    if (isCollapsed) {
      return (
        <button
          onClick={(e) => { e.stopPropagation(); goToProfile(); }}
          onMouseEnter={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            setNavTip({ name: "Profile", y: r.top + r.height / 2 });
          }}
          onMouseLeave={() => setNavTip(null)}
          className="flex items-center justify-center py-2 cursor-pointer"
          aria-label="Profile"
        >
          <span className="flex items-center justify-center w-7 h-7 rounded-full bg-[#1F2753] text-white text-[11px] font-semibold flex-shrink-0">
            {initial}
          </span>
        </button>
      );
    }

    return (
      <button
        onClick={goToProfile}
        className="w-full flex items-center gap-2 p-2.5 hover:bg-[#f1ebdf] transition-colors text-left"
      >
        <span className="flex items-center justify-center w-7 h-7 rounded-full bg-[#1F2753] text-white text-[11px] font-semibold flex-shrink-0">
          {initial}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-[11px] font-medium text-gray-900 truncate">{user.email}</p>
            {planLabel && (
              <span className={`shrink-0 px-1.5 py-[1px] rounded-full text-[9px] font-semibold ${planColorClass}`}>
                {planLabel}
              </span>
            )}
          </div>
          {secondaryLine && (
            <p
              className={`text-[10px] truncate ${isTrialPlan ? "text-red-400" : "text-gray-500"}`}
              title={profile?.org_name ? undefined : (profile?.is_org_member ? profile?.org_trial_end_date : profile?.trial_end_date)}
            >
              {secondaryLine}
            </p>
          )}
        </div>
      </button>
    );
  };

  const renderDemoActions = () => {
    if (!isLiveDemo || isCollapsed) return null;

    return (
      <div className="space-y-2 p-3">
        <div className="mb-1 flex items-center justify-center">
          <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#1F2753]/70">
            Live demo
          </span>
        </div>
        <button
          type="button"
          onClick={() => navigate("/login")}
          className="inline-flex h-9 w-full items-center justify-center rounded-md bg-[#1F2753] text-sm font-medium text-[#FCFAF4] shadow-sm transition-colors hover:bg-[#1a2248] focus:outline-none focus:ring-2 focus:ring-[#1F2753]/30"
        >
          Log In
        </button>
        <button
          type="button"
          onClick={() =>
            window.open(
              "https://calendly.com/nomadic-ai/nomadic-intro",
              "_blank",
            )
          }
          className="inline-flex h-9 w-full items-center justify-center rounded-md border border-[#1F2753] bg-transparent text-sm font-medium text-[#1F2753] transition-colors hover:bg-[#1F2753]/[0.04] focus:outline-none focus:ring-2 focus:ring-[#1F2753]/20"
        >
          Book a Demo
        </button>
      </div>
    );
  };

  return (
    <div
      className={`flex flex-col h-full ${onClose ? "w-full" : isCollapsed ? "w-16" : "w-48"} bg-[#FCFAF4] border-r border-[#e2dccd] transition-all duration-300`}
      // Hovering anywhere on the rail expands it. It then stays open while the
      // user moves around and navigates; selecting a nav item is what collapses
      // it again (see handleNavigation). Clicking a collapsed rail also expands
      // it as a no-hover (touch) fallback. onCollapse is a toggle, so guard on
      // the current collapsed state to avoid double-toggling.
      onMouseEnter={isCollapsed && onCollapse ? () => onCollapse() : undefined}
      onClick={isCollapsed && onCollapse ? () => onCollapse() : undefined}
      style={isCollapsed && onCollapse ? { cursor: "e-resize" } : undefined}
    >
      {renderHeader()}

      <div
        onScroll={handleNavScroll}
        className={`flex-1 overflow-y-auto px-3 pt-4 pb-4 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:transition-colors ${
          isNavScrolling
            ? "[&::-webkit-scrollbar-thumb]:bg-[#f4f0e6]"
            : "[&::-webkit-scrollbar-thumb]:bg-transparent"
        }`}
        style={{
          scrollbarWidth: "thin",
          scrollbarColor: isNavScrolling ? "#f4f0e6 transparent" : "transparent transparent",
        }}
      >
        {renderSection(playgroundSection, { first: true })}
        {renderSection(helpSection)}
        {showAdminSection && renderSection(ADMIN_SECTION)}
      </div>

      {/* Full-bleed divider after the nav (edge-to-edge). */}
      <div className="border-t border-[#e2dccd]" />

      {navTip && isCollapsed && (
        <div
          className="pointer-events-none fixed z-[60] whitespace-nowrap rounded-md bg-[#1F2753] px-2.5 py-1 text-xs font-medium text-white shadow-lg"
          style={{ left: 68, top: navTip.y, transform: "translateY(-50%)" }}
        >
          {navTip.name}
        </div>
      )}

      {isWhiteLabel && !isCollapsed && (
        <div className="flex items-center justify-center gap-1.5 pb-3">
          <span className="text-xs text-gray-500">Powered by</span>
          <img src="/Nomadic_Logo_Only_Latest.svg" alt="Nomadic" className="h-4 w-auto" />
          <span className="text-xs font-medium text-gray-500">Nomadic</span>
        </div>
      )}

      {renderUserFooter()}
      {renderDemoActions()}
    </div>
  );
}
